import clsx from 'clsx'
import { rem } from 'polished'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import { NavWrapper } from 'components/OfferPage/styles'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  arePropertyRatesLoading,
  getPropertyCheapestRates,
  getPropertyFromDurationLabel,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'

const Container = styled(NavWrapper)`
  &.hideOnMobile.hideOnMobile {
    display: none;
    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  flex-wrap: wrap;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    margin-left: auto;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const MobileNewLine = styled.br`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  margin-left: auto;
  justify-content: flex-end;
  position: relative;

  & > * + * {
    margin-left: ${rem(8)};
  }
`
const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

interface Props {
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  areAnytimeRates: boolean
  shouldShowAnytimeRate: boolean
  isLoading: boolean
  headlessMode: boolean
  durationLabel: string
  availableForPurchase?: boolean
  cheapestPropertyRates: [App.PropertyRate | App.Property.AnytimeRate | undefined, App.PropertyRate | undefined]
}

function PropertyStickyHeader(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    areAnytimeRates,
    shouldShowAnytimeRate,
    isLoading,
    headlessMode,
    durationLabel,
    availableForPurchase,
    cheapestPropertyRates,
  } = props

  const intl = useIntl()
  const [cheapestHotelOnlyRate] = cheapestPropertyRates

  const hideAnytimePrices = areAnytimeRates && !shouldShowAnytimeRate
  const hasHotelPrice = !hideAnytimePrices && !!cheapestHotelOnlyRate

  const hotelPrice = cheapestHotelOnlyRate?.totals.displayPrice ?? 0
  const memberPrice = cheapestHotelOnlyRate?.luxPlusTotals?.displayPrice ?? 0

  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }
  const displaySoldOutLabel = !availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED
  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice = !!memberPrice && memberPrice < hotelPrice

  return (
    <Container className={clsx({ hideOnMobile: hideSoldOutOnMobile })}>
      <Root>
        <div>
          {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
        </div>
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden} wall={<PriceWrapper><GenericLoginSignupWall /></PriceWrapper>}>
            <PriceWrapper>
              {hasHotelPrice && hotelPrice > 0 && <Group direction="vertical" horizontalAlign="end">
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <LuxPlusPriceGuard
                  showMemberPrice={showMemberPrice}
                  fallbackPrice={<PriceRowPrice
                    data-testid="property-sticky-header-price"
                    size="S"
                    price={hotelPrice}
                    saleUnit="total"
                  />}
                >
                  <PriceRowLuxPlusPrice
                    size="M"
                    memberPrice={memberPrice}
                    saleUnit="total"
                    logoPlacement="leading"
                  />
                </LuxPlusPriceGuard>
                <LoadingIndicator visible={isLoading} floating opaque />
              </Group>}
              <TextButton kind="primary" data-testid="offer-view-options" onClick={onViewOptionsClick}>
                <CSSBreakpoint max="tablet" as="span">
                  {intl.formatMessage({
                    id: 'lIrp9DNfphqQDJ9365rN',
                    defaultMessage: 'View options',
                    description: 'Button text for mobile view to view package options on property sticky header',
                  })}
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop" as="span">
                  {intl.formatMessage({
                    id: 'Q6ZdGCm1IYrvKc52baKc',
                    defaultMessage: 'View package options',
                    description: 'Button text for desktop view to view package options on property sticky header',
                  })}
                </CSSBreakpoint>
              </TextButton>
            </PriceWrapper>
          </WalledContent>
        )}
        {!availableForPurchase && displaySoldOutLabel &&
          <InlineWrapper>
            <BodyText variant="medium" weight="bold" colour="neutral-one">
              <FormattedMessage
                id="Ciqto2iTzNKZjdENkEhO"
                defaultMessage="This offer has{lineBreak} SOLD OUT"
                description="Sold out message on property sticky header with line break for mobile"
                values={{
                  lineBreak: <MobileNewLine />,
                }}
              />
            </BodyText>
            <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
              {intl.formatMessage({
                id: 'zxYSmXQsFvrHw5POjV-q',
                defaultMessage: 'Browse all hotel offers',
                description: 'Link text to browse hotels when offer is sold out on property sticky header',
              })}
            </TextButton>
          </InlineWrapper>}
      </Root>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  // We only need to show anytime pricing for traffic coming from marketing, which will have
  // `utm_bp` set.
  shouldShowAnytimeRate: !!appState.utm.bp,
  isLoading: arePropertyRatesLoading(state, appState),
  headlessMode: appState.system.headlessMode,
  durationLabel: getPropertyFromDurationLabel(state, appState),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  cheapestPropertyRates: getPropertyCheapestRates(state, appState),
  open: !!state.activeFilter,
}), PropertyStickyHeader, preventRenderWithOpenFilter)
